import React, { useState, useRef } from 'react'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Upload, X, User, AlertCircle } from 'lucide-react'
import { getEmployeePictureUploadUrl, uploadEmployeePictureToS3 } from '@/api/workforce'

interface ImageUploadProps {
  employeeId: string
  currentImageUrl?: string
  onUploadComplete: (pictureKey: string, pictureUrl: string) => void
  onError?: (error: string) => void
  className?: string
}

/**
 * Reusable image upload component following S3 secure upload pattern
 *
 * Workflow:
 * 1. User selects image file
 * 2. Request presigned URL from backend
 * 3. Upload directly to S3 using presigned URL
 * 4. Return S3 key to parent component for storage
 */
export function ImageUpload({
  employeeId,
  currentImageUrl,
  onUploadComplete,
  onError,
  className = ''
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(currentImageUrl)
  const [error, setError] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = 'Formato de imagem inválido. Use JPG, PNG ou WebP.'
      setError(errorMsg)
      onError?.(errorMsg)
      return
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      const errorMsg = 'Imagem muito grande. Tamanho máximo: 5MB.'
      setError(errorMsg)
      onError?.(errorMsg)
      return
    }

    // Clear previous errors
    setError('')

    try {
      setUploading(true)

      // Create local preview
      const objectUrl = URL.createObjectURL(file)
      setPreviewUrl(objectUrl)

      // Step 1: Get presigned URL from backend
      const extension = `.${file.name.split('.').pop()?.toLowerCase() || 'jpg'}`
      const uploadData = await getEmployeePictureUploadUrl(employeeId, {
        mimeType: file.type,
        extension: extension,
        originalFilename: file.name,
        contentLength: file.size
      })

      // Step 2: Upload directly to S3
      await uploadEmployeePictureToS3(uploadData.signedUrl, file)

      // Step 3: Store the S3 key - backend will generate signed URLs when retrieving employee data
      // We pass an empty string for pictureUrl since it will be signed by the backend
      onUploadComplete(uploadData.key, '')

      // Clean up object URL
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      console.error('Error uploading image:', err)
      const errorMsg = err instanceof Error ? err.message : 'Erro ao fazer upload da imagem'
      setError(errorMsg)
      onError?.(errorMsg)
      setPreviewUrl(currentImageUrl)
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = () => {
    setPreviewUrl(undefined)
    setError('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-brand-gray mb-2">
        Foto do Colaborador
      </label>

      <div className="flex items-start gap-4">
        {/* Preview */}
        <div className="relative">
          <div className="w-24 h-24 rounded-monumenta bg-brand-darker border-2 border-brand-dark overflow-hidden flex items-center justify-center">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-12 h-12 text-brand-gray" />
            )}
          </div>

          {uploading && (
            <div className="absolute inset-0 bg-black/50 rounded-monumenta flex items-center justify-center">
              <LoadingSpinner size="sm" />
            </div>
          )}

          {previewUrl && !uploading && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 bg-error text-white rounded-full p-1 hover:bg-error/80 transition-colors"
              aria-label="Remover imagem"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Upload Button */}
        <div className="flex-1 space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />

          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleClick}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Enviando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {previewUrl ? 'Alterar Foto' : 'Enviar Foto'}
              </>
            )}
          </Button>

          <p className="text-xs text-brand-gray">
            JPG, PNG ou WebP. Máximo 5MB.
          </p>

          {error && (
            <div className="flex items-start gap-2 p-2 bg-error/20 border border-error/30 rounded-monumenta">
              <AlertCircle className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
              <p className="text-xs text-error">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
